/**
 * Feedback engine — turns a GradeResult into a structured FeedbackEvent (PRD §7.4).
 * Every event is reconstructable from an Attempt + the pinned content version, so it
 * must be a pure function of its inputs.
 */

import { fnv1a } from './determinism.js';
import { repairFor } from './misconception.js';
import type { GradeResult, MisconceptionTag, ResultStatus } from './models.js';

export interface FeedbackEvent {
  readonly response_id: string;
  readonly result: ResultStatus;
  readonly correct_components: readonly string[];
  readonly missing_components: readonly string[];
  readonly severity: 'low' | 'medium' | 'high';
  readonly misconception_tags: readonly MisconceptionTag[];
  readonly repair_action: string | null;
  readonly review_cards_created: readonly string[];
}

function severityFor(status: ResultStatus): FeedbackEvent['severity'] {
  if (status === 'incorrect') return 'high';
  if (status === 'partially_correct') return 'medium';
  return 'low';
}

/**
 * Build the feedback event. A miss with a misconception produces a repair action and a
 * deterministic review-card id per tag (so the same miss always schedules the same card).
 */
export function buildFeedbackEvent(input: {
  responseId: string;
  grade: GradeResult;
}): FeedbackEvent {
  const { responseId, grade } = input;
  const missed = grade.status !== 'correct';
  const tags = grade.misconceptionTags;

  const reviewCards = missed
    ? tags.map((t) => `card_${t}_${fnv1a(`${responseId}|${t}`).toString(36)}`)
    : [];

  return {
    response_id: responseId,
    result: grade.status,
    correct_components: grade.correctComponents,
    missing_components: grade.missingComponents,
    severity: severityFor(grade.status),
    misconception_tags: tags,
    repair_action: missed && tags.length > 0 ? repairFor(tags[0]!) : null,
    review_cards_created: reviewCards,
  };
}
